import React, { useEffect, useState } from 'react';
import {
  X,
  FileText,
  AlertTriangle,
  Clock,
  Shield,
  Plane,
  MapPin,
  ExternalLink,
  Building,
  CheckCircle2,
  Calendar,
  Tag,
  Edit2,
  Info,
  Mail,
  Send,
} from 'lucide-react';
import { useEscapeKey } from '../shared/useGlobalModalEscape';
import { ingestionService } from '../modules/ingestion/ingestionService';
import { SafetyReport } from '../modules/ingestion/types';
import {
  WAITING_ASSESS_HIGH_DATA,
  WAITING_ASSESS_MED_DATA,
  WAITING_ASSESS_LOW_DATA,
  OVERDUE_REPORTS_DATA,
  IN_PROGRESS_REPORTS_DATA,
  PENDING_REPORTS_DATA
} from '../modules/dashboard/dashboardIndicatorData';

export interface ReportDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId?: string | null;
  report?: SafetyReport | null;
  initialEditMode?: boolean;
  onNavigateToIngestion?: (reportId: string) => void;
  onReportUpdated?: (updatedReport: SafetyReport) => void;
}

export const ReportDetailsModal: React.FC<ReportDetailsModalProps> = ({
  isOpen,
  onClose,
  reportId,
  report: initialReport,
  initialEditMode = false,
  onNavigateToIngestion,
  onReportUpdated,
}) => {
  useEscapeKey(onClose, isOpen);

  const [report, setReport] = useState<SafetyReport | null>(initialReport || null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Reporter Edit Mode states (accessible prior to SMS assessment)
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editTitle, setEditTitle] = useState<string>('');
  const [editDescription, setEditDescription] = useState<string>('');
  const [editEventDate, setEditEventDate] = useState<string>('');
  const [editLocation, setEditLocation] = useState<string>('');
  const [editFlightNumber, setEditFlightNumber] = useState<string>('');
  const [editAircraftReg, setEditAircraftReg] = useState<string>('');
  const [editSeverity, setEditSeverity] = useState<string>('MEDIUM');
  const [isSavingEdit, setIsSavingEdit] = useState<boolean>(false);
  const [editSuccessMsg, setEditSuccessMsg] = useState<string | null>(null);
  const [editErrorMsg, setEditErrorMsg] = useState<string | null>(null);

  // Mail to Reporter states
  const [showMailModal, setShowMailModal] = useState<boolean>(false);
  const [mailRecipient, setMailRecipient] = useState<string>('');
  const [mailSubject, setMailSubject] = useState<string>('');
  const [mailMessage, setMailMessage] = useState<string>('');
  const [isSendingMail, setIsSendingMail] = useState<boolean>(false);
  const [mailStatusMsg, setMailStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleOpenMailModal = () => {
    if (!report) return;
    const defaultEmail =
      report.reporter_identity?.email ||
      (report as any).reporter_email ||
      (report.created_by?.includes('@') ? report.created_by : '');
    setMailRecipient(defaultEmail);
    setMailSubject(`[SafetyCore SMS] Feedback on Safety Report #${report.report_number} - ${report.title}`);
    setMailMessage('');
    setMailStatusMsg(null);
    setShowMailModal(true);
  };

  const handleApplyMailTemplate = (type: 'DETAILS' | 'ASSESSMENT' | 'ACTION') => {
    if (!report) return;
    if (type === 'DETAILS') {
      setMailSubject(`[SafetyCore SMS] Request for Information: Report #${report.report_number}`);
      setMailMessage(
        `Dear Reporter,\n\nThank you for submitting safety report #${report.report_number} regarding "${report.title}".\n\nIn order to complete the safety assessment and determine appropriate corrective actions, could you please provide additional details regarding the event sequence, operating conditions, and any immediate actions taken?\n\nThank you for supporting our organization's safety standards.\n\nSincerely,\nSafety Management System (SMS) Desk`
      );
    } else if (type === 'ASSESSMENT') {
      setMailSubject(`[SafetyCore SMS] Status Update: Safety Report #${report.report_number}`);
      setMailMessage(
        `Dear Reporter,\n\nWe would like to inform you that your safety report #${report.report_number} ("${report.title}") has been received and is currently under active safety risk assessment by the SMS Safety Desk.\n\nYou will receive further updates as the assessment progresses.\n\nThank you for your active participation in our reporting system.\n\nSafety & Quality Operations`
      );
    } else if (type === 'ACTION') {
      setMailSubject(`[SafetyCore SMS] Occurrence & Action Update: Report #${report.report_number}`);
      setMailMessage(
        `Dear Reporter,\n\nYour safety report #${report.report_number} ("${report.title}") has been assessed by the Safety Desk. An Occurrence record has been opened and corrective/preventive actions are being assigned to the responsible operational department.\n\nWe appreciate your vigilance and dedication to aviation safety.\n\nSafety Management System Team`
      );
    }
  };

  const handleSendMailToReporter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!report) return;
    if (!mailRecipient.trim()) {
      setMailStatusMsg({ type: 'error', text: 'Please enter a valid recipient email address.' });
      return;
    }
    if (!mailMessage.trim()) {
      setMailStatusMsg({ type: 'error', text: 'Message body cannot be empty.' });
      return;
    }

    try {
      setIsSendingMail(true);
      setMailStatusMsg(null);
      const res = await ingestionService.mailToReporter(report.id, {
        recipientEmail: mailRecipient.trim(),
        subject: mailSubject.trim(),
        message: mailMessage.trim(),
      });
      setMailStatusMsg({
        type: 'success',
        text: `✓ Email successfully sent to ${res.sentTo} and logged in the report audit trail!`,
      });
      try {
        const fresh = await ingestionService.getReportById(report.id);
        if (fresh) {
          setReport(fresh);
          onReportUpdated?.(fresh);
        }
      } catch {}
      setTimeout(() => {
        setShowMailModal(false);
      }, 1600);
    } catch (err: any) {
      setMailStatusMsg({
        type: 'error',
        text: err?.message || 'Failed to dispatch email to reporter.',
      });
    } finally {
      setIsSendingMail(false);
    }
  };

  const populateEditFields = (r: SafetyReport) => {
    setEditTitle(r.title || '');
    setEditDescription(r.description || '');
    setEditEventDate(r.event_date ? new Date(r.event_date).toISOString().substring(0, 16) : '');
    setEditLocation(r.event_location || r.airport_code || '');
    setEditFlightNumber(r.flight_number || '');
    setEditAircraftReg(r.aircraft_registration || '');
    setEditSeverity(r.initial_severity || 'MEDIUM');
    setEditErrorMsg(null);
  };

  useEffect(() => {
    if (!isOpen) {
      setReport(null);
      setError(null);
      setLoading(false);
      setIsEditing(false);
      setEditSuccessMsg(null);
      setEditErrorMsg(null);
      return;
    }

    if (initialReport) {
      setReport(initialReport);
      if (initialEditMode && (initialReport.status === 'SUBMITTED' || initialReport.status === 'WAITING_ASSESSMENT' || initialReport.status === 'DRAFT')) {
        populateEditFields(initialReport);
        setIsEditing(true);
      }
      // If we have an initial report with an id, optionally refresh in background for status history
      if (initialReport.id) {
        ingestionService
          .getReportById(initialReport.id)
          .then((full) => {
            if (full) {
              setReport(full);
              if (initialEditMode && (full.status === 'SUBMITTED' || full.status === 'WAITING_ASSESSMENT' || full.status === 'DRAFT')) {
                populateEditFields(full);
              }
            }
          })
          .catch(() => {
            // Keep initialReport on background refresh failure
          });
      }
      return;
    }

    if (reportId) {
      setLoading(true);
      setError(null);
      ingestionService
        .getReportById(reportId)
        .then((data) => {
          if (data) {
            setReport(data);
            if (initialEditMode && (data.status === 'SUBMITTED' || data.status === 'WAITING_ASSESSMENT' || data.status === 'DRAFT')) {
              populateEditFields(data);
              setIsEditing(true);
            }
          } else {
            setError('Report not found or access restricted.');
          }
        })
        .catch((err: any) => {
          const allIndicatorMocks = [
            ...WAITING_ASSESS_HIGH_DATA,
            ...WAITING_ASSESS_MED_DATA,
            ...WAITING_ASSESS_LOW_DATA,
            ...OVERDUE_REPORTS_DATA,
            ...IN_PROGRESS_REPORTS_DATA,
            ...PENDING_REPORTS_DATA
          ];
          const mockMatch = allIndicatorMocks.find((m) => m.id === reportId || m.targetId === reportId);
          if (mockMatch) {
            const mockRep = {
              id: mockMatch.id,
              report_number: mockMatch.id,
              title: mockMatch.title,
              description: mockMatch.summary || mockMatch.title,
              report_category: 'SAFETY',
              report_type: mockMatch.category || 'OCCURRENCE',
              status: mockMatch.status || 'SUBMITTED',
              initial_severity: (mockMatch.severity as any) || 'HIGH',
              event_date: new Date().toISOString(),
              created_at: new Date().toISOString()
            } as any;
            setReport(mockRep);
            if (initialEditMode) {
              populateEditFields(mockRep);
              setIsEditing(true);
            }
          } else {
            setError(err?.message || 'Failed to load report details.');
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isOpen, reportId, initialReport, initialEditMode]);

  const canEdit = !!report && (report.status === 'SUBMITTED' || report.status === 'WAITING_ASSESSMENT' || report.status === 'DRAFT');

  const handleStartEditing = () => {
    if (!report) return;
    populateEditFields(report);
    setIsEditing(true);
    setEditSuccessMsg(null);
    setEditErrorMsg(null);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setEditErrorMsg(null);
  };

  const handleSaveEditing = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!report) return;
    setIsSavingEdit(true);
    setEditErrorMsg(null);
    try {
      const updated = await ingestionService.updateReport(report.id, {
        title: editTitle.trim(),
        description: editDescription.trim(),
        event_date: editEventDate ? new Date(editEventDate).toISOString() : report.event_date,
        event_location: editLocation.trim(),
        airport_code: editLocation.trim() ? editLocation.trim().substring(0, 4).toUpperCase() : undefined,
        flight_number: editFlightNumber.trim() || undefined,
        aircraft_registration: editAircraftReg.trim() || undefined,
        initial_severity: editSeverity as any,
      });

      // Fetch fresh report to get updated audit history
      const fullFresh = await ingestionService.getReportById(report.id).catch(() => updated);
      const finalReport = fullFresh || updated;
      setReport(finalReport);
      setIsEditing(false);
      setEditSuccessMsg('Rapor başarıyla güncellendi ve düzenleme işlemi denetim geçmişine (Audit Log) kaydedildi.');
      if (onReportUpdated) {
        onReportUpdated(finalReport);
      }
    } catch (err: any) {
      setEditErrorMsg(err?.message || 'Rapor güncellenirken bir hata oluştu.');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleNavigateToOccurrence = (occIdOrNum?: string) => {
    onClose();
    const params = new URLSearchParams();
    params.set('tab', 'occurrences');
    const occNum = occIdOrNum && (occIdOrNum.startsWith('OCR-') || occIdOrNum.startsWith('OCC-')) ? occIdOrNum : report?.occurrence_number;
    const occId = occIdOrNum && !occIdOrNum.startsWith('OCR-') && !occIdOrNum.startsWith('OCC-') ? occIdOrNum : report?.occurrence_id;
    if (occId) {
      params.set('occurrenceId', occId);
    }
    if (occNum) {
      params.set('occurrenceNumber', occNum);
    }
    const newUrl = `?${params.toString()}`;
    window.history.pushState({ tab: 'occurrences' }, '', newUrl);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleNavigateToInvestigation = (invIdOrNum?: string) => {
    onClose();
    const params = new URLSearchParams();
    params.set('tab', 'investigations');
    const invNum = invIdOrNum && invIdOrNum.startsWith('INV-') ? invIdOrNum : report?.investigation_number;
    const invId = invIdOrNum && !invIdOrNum.startsWith('INV-') ? invIdOrNum : report?.investigation_id;
    if (invId) {
      params.set('investigationId', invId);
    }
    if (invNum) {
      params.set('investigationNumber', invNum);
    }
    const newUrl = `?${params.toString()}`;
    window.history.pushState({ tab: 'investigations' }, '', newUrl);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleNavigateToSafetyReports = (reportIdOrNumber?: string) => {
    const targetId = reportIdOrNumber || report?.id || report?.report_number;
    if (onNavigateToIngestion && targetId) {
      onNavigateToIngestion(targetId);
      return;
    }
    onClose();
    const params = new URLSearchParams();
    params.set('tab', 'ingestion');
    if (targetId) {
      params.set('reportId', targetId);
    }
    const newUrl = `?${params.toString()}`;
    window.history.pushState({ tab: 'ingestion' }, '', newUrl);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  if (!isOpen) return null;

  const currentTab = new URLSearchParams(window.location.search).get('tab') || 'dashboard';
  const isCurrentlyInSafetyReports = currentTab === 'ingestion';

  const isNonSafety = report?.report_category === 'NON_SAFETY';
  const wasReclassified =
    report?.original_report_category &&
    (report.original_report_category !== report.report_category ||
      report.original_report_type !== report.report_type);

  const getSeverityStyle = (severity?: string) => {
    const sev = (severity || 'MEDIUM').toUpperCase();
    if (sev === 'CRITICAL') return { bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5' };
    if (sev === 'HIGH') return { bg: '#ffedd5', text: '#c2410c', border: '#fdba74' };
    if (sev === 'LOW') return { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' };
    return { bg: '#fef3c7', text: '#b45309', border: '#fcd34d' };
  };

  const getStatusStyle = (status?: string) => {
    const s = (status || 'SUBMITTED').toUpperCase();
    if (s === 'CLOSED') return { bg: '#dcfce7', text: '#15803d', border: '#86efac' };
    if (s === 'VALIDATION' || s === 'UNDER_REVIEW')
      return { bg: '#eff6ff', text: '#1d4ed8', border: '#93c5fd' };
    if (s === 'REJECTED') return { bg: '#fef2f2', text: '#b91c1c', border: '#fca5a5' };
    return { bg: '#fef9c3', text: '#854d0e', border: '#fde047' };
  };

  const sevStyle = getSeverityStyle(report?.initial_severity);
  const statStyle = getStatusStyle(report?.status);

  return (
    <div
      className="modal-backdrop"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(3px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-card"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '860px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          animation: 'fadeInModal 0.2s ease-out',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '18px 24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#ffffff',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '8px',
                backgroundColor: isNonSafety ? '#e0e7ff' : '#eff6ff',
                color: isNonSafety ? '#4338ca' : '#1d4ed8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FileText size={20} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: '17px',
                    fontWeight: 700,
                    color: '#0f172a',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                  }}
                >
                  Report Assessment — {report?.report_number || report?.id || reportId || 'Details'}
                </h3>
              </div>
              <span style={{ fontSize: '12px', color: '#64748b' }}>
                {report?.created_at
                  ? `Submitted on ${new Date(report.created_at).toLocaleString()}`
                  : 'Report Details & Assessment Status'}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '6px',
              color: '#94a3b8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="Close (Esc)"
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <div
          style={{
            padding: '20px 24px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {loading ? (
            <div
              style={{
                textAlign: 'center',
                padding: '48px 24px',
                color: '#64748b',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  border: '3px solid #e2e8f0',
                  borderTopColor: '#2563eb',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
              <span style={{ fontSize: '14px', fontWeight: 500 }}>Loading report details...</span>
            </div>
          ) : error ? (
            <div
              style={{
                padding: '24px',
                textAlign: 'center',
                background: '#fef2f2',
                borderRadius: '8px',
                border: '1px solid #fee2e2',
                color: '#991b1b',
              }}
            >
              <AlertTriangle size={28} style={{ margin: '0 auto 8px', color: '#dc2626' }} />
              <div style={{ fontWeight: 600, fontSize: '15px' }}>Unable to load report</div>
              <p style={{ margin: '4px 0 0', fontSize: '13px' }}>{error}</p>
            </div>
          ) : !report ? (
            <div style={{ textAlign: 'center', padding: '36px', color: '#64748b' }}>
              No report information available.
            </div>
          ) : (
            <>
              {/* Badges / Status Metadata Strip */}
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  padding: '12px 16px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                }}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    borderRadius: '9999px',
                    fontSize: '11px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    backgroundColor: isNonSafety ? '#e0e7ff' : '#dbeafe',
                    color: isNonSafety ? '#4338ca' : '#1d4ed8',
                  }}
                >
                  {isNonSafety ? 'NON-SAFETY REPORT (QMS / OPS)' : 'SAFETY REPORT (SMS)'}
                </span>

                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '4px 10px',
                    borderRadius: '9999px',
                    fontSize: '11px',
                    fontWeight: 600,
                    backgroundColor: '#e2e8f0',
                    color: '#334155',
                  }}
                >
                  TYPE: {report.report_type || 'GENERAL'}
                </span>

                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '4px 10px',
                    borderRadius: '9999px',
                    fontSize: '11px',
                    fontWeight: 700,
                    backgroundColor: sevStyle.bg,
                    color: sevStyle.text,
                    border: `1px solid ${sevStyle.border}`,
                  }}
                >
                  SEVERITY: {report.initial_severity || 'MEDIUM'}
                </span>

                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '4px 10px',
                    borderRadius: '9999px',
                    fontSize: '11px',
                    fontWeight: 700,
                    backgroundColor: statStyle.bg,
                    color: statStyle.text,
                    border: `1px solid ${statStyle.border}`,
                  }}
                >
                  STATUS: {report.status === 'SUBMITTED' || report.status === 'WAITING_ASSESSMENT' ? 'Waiting Assessment' : (report.status || 'Waiting Assessment')}
                </span>

                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    borderRadius: '9999px',
                    fontSize: '11px',
                    fontWeight: 600,
                    backgroundColor: '#f1f5f9',
                    color: '#475569',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Shield size={12} />
                  IDENTITY: {report.identity_mode || 'IDENTIFIED'}
                </span>

                {/* Pathway / Outcome Navigation Badges */}
                {Boolean(report.occurrence_number || report.occurrence_id || report.assessment_outcome === 'CORRECTIVE_ACTION' || report.assessment_outcome === 'OCCURRENCE') && (
                  <button
                    type="button"
                    onClick={() => handleNavigateToOccurrence(report.occurrence_number || report.occurrence_id)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 10px',
                      borderRadius: '9999px',
                      fontSize: '11px',
                      fontWeight: 700,
                      backgroundColor: '#fffbeb',
                      color: '#b45309',
                      border: '1px solid #fde68a',
                      cursor: 'pointer',
                    }}
                    title="Occurrences modülünde ilgili Olay kaydına git"
                  >
                    ⚡ {report.occurrence_number || 'Occurrence'} ↗
                  </button>
                )}

                {Boolean(report.investigation_number || report.investigation_id || report.assessment_outcome === 'INVESTIGATION') && (
                  <button
                    type="button"
                    onClick={() => handleNavigateToInvestigation(report.investigation_number || report.investigation_id)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 10px',
                      borderRadius: '9999px',
                      fontSize: '11px',
                      fontWeight: 700,
                      backgroundColor: '#faf5ff',
                      color: '#7e22ce',
                      border: '1px solid #d8b4fe',
                      cursor: 'pointer',
                    }}
                    title="Safety Investigations modülünde ilgili İnceleme kaydına git"
                  >
                    🔍 {report.investigation_number || `INV-${report.report_number}`} ↗
                  </button>
                )}

                {report.assessment_outcome === 'INFO_ONLY' && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 10px',
                      borderRadius: '9999px',
                      fontSize: '11px',
                      fontWeight: 600,
                      backgroundColor: '#ecfdf5',
                      color: '#065f46',
                      border: '1px solid #a7f3d0',
                    }}
                    title={`Info Only Record (Ref: ${report.info_reference_number || report.report_number})`}
                  >
                    ✓ Info Only
                  </span>
                )}

                {!isCurrentlyInSafetyReports && (
                  <button
                    type="button"
                    onClick={() => handleNavigateToSafetyReports(report.id || report.report_number)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 10px',
                      borderRadius: '9999px',
                      fontSize: '11px',
                      fontWeight: 700,
                      backgroundColor: '#eff6ff',
                      color: '#1d4ed8',
                      border: '1px solid #bfdbfe',
                      cursor: 'pointer',
                    }}
                    title="Safety Reports modülünde bu raporu aç"
                  >
                    📄 Safety Reports'ta Aç ↗
                  </button>
                )}

                {canEdit && (
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                    {!isEditing ? (
                      <button
                        type="button"
                        onClick={handleStartEditing}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '5px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 600,
                          backgroundColor: '#fffbeb',
                          color: '#b45309',
                          border: '1px solid #fde68a',
                          cursor: 'pointer',
                        }}
                        title="Rapor SMS değerlendirmesine alınana kadar içeriğini düzenleyebilirsiniz"
                      >
                        <Edit2 size={13} />
                        <span>Edit Report</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleCancelEditing}
                        disabled={isSavingEdit}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '5px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 600,
                          backgroundColor: '#f1f5f9',
                          color: '#475569',
                          border: '1px solid #cbd5e1',
                          cursor: 'pointer',
                        }}
                      >
                        <span>Cancel Edit</span>
                      </button>
                    )}
                  </div>
                )}
              </div>

              {editSuccessMsg && (
                <div
                  style={{
                    padding: '10px 16px',
                    borderRadius: '6px',
                    background: '#ecfdf5',
                    border: '1px solid #a7f3d0',
                    color: '#065f46',
                    fontSize: '13px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircle2 size={16} color="#10b981" />
                    {editSuccessMsg}
                  </span>
                  <button
                    type="button"
                    onClick={() => setEditSuccessMsg(null)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#065f46', fontWeight: 700 }}
                  >
                    ✕
                  </button>
                </div>
              )}

              {isEditing ? (
                /* Reporter Edit Form prior to SMS assessment */
                <form onSubmit={handleSaveEditing} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div
                    style={{
                      padding: '12px 16px',
                      background: '#eff6ff',
                      borderLeft: '4px solid #3b82f6',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    <Info size={18} color="#2563eb" style={{ flexShrink: 0 }} />
                    <div style={{ fontSize: '12.5px', color: '#1e40af', lineHeight: 1.4 }}>
                      <strong>SMS Değerlendirmesi Öncesi Düzenleme:</strong> Bu rapor henüz SMS yetkilisi tarafından değerlendirmeye alınmadığı için içeriğini güncelleyebilirsiniz. Yapılan tüm değişiklikler sistem denetim geçmişinde (Audit Trail) tarih ve kullanıcı bilgisiyle loglanır.
                    </div>
                  </div>

                  {editErrorMsg && (
                    <div
                      style={{
                        padding: '10px 14px',
                        borderRadius: '6px',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#991b1b',
                        fontSize: '13px',
                        fontWeight: 600,
                      }}
                    >
                      ⚠️ {editErrorMsg}
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>
                      Subject / Event Title <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '9px 12px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Occurrence Date & Time</label>
                      <input
                        type="datetime-local"
                        value={editEventDate}
                        onChange={(e) => setEditEventDate(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                        }}
                      />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Location / Aerodrome</label>
                      <input
                        type="text"
                        value={editLocation}
                        onChange={(e) => setEditLocation(e.target.value)}
                        placeholder="e.g. LTFM (Istanbul Airport)"
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Flight Number</label>
                      <input
                        type="text"
                        value={editFlightNumber}
                        onChange={(e) => setEditFlightNumber(e.target.value)}
                        placeholder="e.g. TK 1824"
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                        }}
                      />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Aircraft Registration</label>
                      <input
                        type="text"
                        value={editAircraftReg}
                        onChange={(e) => setEditAircraftReg(e.target.value)}
                        placeholder="e.g. TC-JHV"
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                        }}
                      />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>Initial Severity</label>
                      <select
                        value={editSeverity}
                        onChange={(e) => setEditSeverity(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '13px',
                          backgroundColor: '#ffffff',
                        }}
                      >
                        <option value="LOW">LOW</option>
                        <option value="MEDIUM">MEDIUM</option>
                        <option value="HIGH">HIGH</option>
                        <option value="CRITICAL">CRITICAL</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#334155' }}>
                      Event Description / Narrative <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <textarea
                      required
                      rows={6}
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        fontSize: '13px',
                        lineHeight: 1.5,
                        fontFamily: 'inherit',
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '6px' }}>
                    <button
                      type="button"
                      onClick={handleCancelEditing}
                      disabled={isSavingEdit}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        backgroundColor: '#ffffff',
                        color: '#475569',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSavingEdit}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 20px',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: '#2563eb',
                        color: '#ffffff',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: isSavingEdit ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {isSavingEdit ? (
                        <>
                          <div
                            style={{
                              width: '14px',
                              height: '14px',
                              border: '2px solid #ffffff',
                              borderTopColor: 'transparent',
                              borderRadius: '50%',
                              animation: 'spin 0.8s linear infinite',
                            }}
                          />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <span>💾 Save Changes & Log Edit</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <>

              {/* Reclassification Alert Notice */}
              {wasReclassified && (
                <div
                  style={{
                    padding: '12px 16px',
                    background: '#fffbeb',
                    borderLeft: '4px solid #f59e0b',
                    borderRadius: '6px',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: '#92400e',
                      fontSize: '13px',
                      marginBottom: '2px',
                    }}
                  >
                    ↺ SMS Assessment Reclassification Note
                  </div>
                  <p style={{ margin: 0, fontSize: '12px', color: '#78350f', lineHeight: 1.5 }}>
                    This report was originally submitted as{' '}
                    <strong>
                      {report.original_report_category} / {report.original_report_type}
                    </strong>{' '}
                    and was officially reclassified to{' '}
                    <strong>
                      {report.report_category} / {report.report_type}
                    </strong>{' '}
                    during SMS assessment review.
                    {report.reclassification_reason
                      ? ` Reason: "${report.reclassification_reason}"`
                      : ''}
                    {report.reclassified_by ? ` (Reviewed by: ${report.reclassified_by})` : ''}
                  </p>
                </div>
              )}

              {/* Linked Pathway & Outcomes Navigation Banner */}
              {Boolean(report.occurrence_number || report.occurrence_id || report.assessment_outcome === 'CORRECTIVE_ACTION' || report.assessment_outcome === 'OCCURRENCE') && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: '#fffbeb',
                    borderRadius: '8px',
                    border: '1px solid #fde68a',
                    marginBottom: '16px',
                    gap: '12px',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '22px' }}>⚡</span>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#92400e', textTransform: 'uppercase' }}>
                        Linked Occurrence (SMS Register)
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#78350f' }}>
                        {report.occurrence_number || 'OCR Linked'}
                        {report.corrective_action_number && (
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#b45309', marginLeft: '8px' }}>
                            • Corrective Action: {report.corrective_action_number}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleNavigateToOccurrence(report.occurrence_number || report.occurrence_id)}
                    style={{
                      background: '#d97706',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '7px 14px',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                    title="Occurrences modülünde ilgili olayı aç"
                  >
                    Open Occurrence in SMS ↗
                  </button>
                </div>
              )}

              {Boolean(report.investigation_number || report.investigation_id || report.assessment_outcome === 'INVESTIGATION') && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: '#faf5ff',
                    borderRadius: '8px',
                    border: '1px solid #d8b4fe',
                    marginBottom: '16px',
                    gap: '12px',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '22px' }}>🔍</span>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#6b21a8', textTransform: 'uppercase' }}>
                        Linked Safety Investigation (ICAO Annex 13)
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#581c87' }}>
                        {report.investigation_number || `INV-${report.report_number}`}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleNavigateToInvestigation(report.investigation_number || report.investigation_id)}
                    style={{
                      background: '#7e22ce',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '7px 14px',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                    title="Safety Investigations modülünde inceleme dosyasını aç"
                  >
                    Open Safety Investigation ↗
                  </button>
                </div>
              )}

              {report.assessment_outcome === 'INFO_ONLY' && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    background: '#ecfdf5',
                    borderRadius: '8px',
                    border: '1px solid #a7f3d0',
                    marginBottom: '16px',
                    gap: '12px',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '20px' }}>✓</span>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#065f46', textTransform: 'uppercase' }}>
                        Info Only Safety Record
                      </div>
                      <div style={{ fontSize: '13px', color: '#047857', fontWeight: 600 }}>
                        Bilgi ve trend analizi amacıyla arşivlenmiştir (Ref: {report.info_reference_number || report.report_number}). Herhangi bir aksiyon veya resmi inceleme gerektirmez.
                      </div>
                    </div>
                  </div>
                  {!isCurrentlyInSafetyReports && (
                    <button
                      type="button"
                      onClick={() => handleNavigateToSafetyReports(report.id || report.report_number)}
                      style={{
                        background: '#059669',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        whiteSpace: 'nowrap',
                      }}
                      title="Safety Reports modülünde bu raporu aç"
                    >
                      Safety Reports'ta Gör ↗
                    </button>
                  )}
                </div>
              )}

              {/* Title & Narrative Section */}
              <div
                style={{
                  padding: '16px',
                  background: '#ffffff',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                }}
              >
                <h4
                  style={{
                    margin: '0 0 10px 0',
                    fontSize: '15px',
                    color: '#0f172a',
                    fontWeight: 700,
                  }}
                >
                  {report.title}
                </h4>
                <div
                  style={{
                    whiteSpace: 'pre-wrap',
                    fontSize: '13px',
                    color: '#334155',
                    lineHeight: 1.6,
                    background: '#f8fafc',
                    padding: '12px 14px',
                    borderRadius: '6px',
                    border: '1px solid #f1f5f9',
                  }}
                >
                  {report.description || 'No detailed narrative description provided.'}
                </div>
              </div>

              {/* Operational Context Grid */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '12px',
                }}
              >
                <div
                  style={{
                    padding: '10px 14px',
                    background: '#f8fafc',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <span
                    style={{
                      fontSize: '11px',
                      color: '#64748b',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <Calendar size={13} /> Event Date & Time
                  </span>
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#0f172a',
                      marginTop: '3px',
                    }}
                  >
                    {report.event_date ? new Date(report.event_date).toLocaleString() : 'N/A'}
                  </div>
                </div>

                <div
                  style={{
                    padding: '10px 14px',
                    background: '#f8fafc',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <span
                    style={{
                      fontSize: '11px',
                      color: '#64748b',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <Plane size={13} /> Flight Number
                  </span>
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#0f172a',
                      marginTop: '3px',
                    }}
                  >
                    {report.flight_number || '—'}
                  </div>
                </div>

                <div
                  style={{
                    padding: '10px 14px',
                    background: '#f8fafc',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <span
                    style={{
                      fontSize: '11px',
                      color: '#64748b',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <Tag size={13} /> Aircraft Registration
                  </span>
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#0f172a',
                      marginTop: '3px',
                    }}
                  >
                    {report.aircraft_registration || '—'}
                  </div>
                </div>

                <div
                  style={{
                    padding: '10px 14px',
                    background: '#f8fafc',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <span
                    style={{
                      fontSize: '11px',
                      color: '#64748b',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <MapPin size={13} /> Airport / Location
                  </span>
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#0f172a',
                      marginTop: '3px',
                    }}
                  >
                    {report.airport_code ? `${report.airport_code} ` : ''}
                    {report.event_location
                      ? `(${report.event_location})`
                      : !report.airport_code
                      ? '—'
                      : ''}
                  </div>
                </div>
              </div>

              {/* SMS Assessment & Report Assignment */}
              <div
                style={{
                  padding: '14px 16px',
                  background: '#f0fdf4',
                  borderRadius: '8px',
                  border: '1px solid #bbf7d0',
                }}
              >
                <h4
                  style={{
                    margin: '0 0 8px 0',
                    fontSize: '12px',
                    color: '#166534',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <Building size={14} /> Report Assignment & Department Action Plan
                </h4>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '10px',
                    fontSize: '13px',
                  }}
                >
                  <div>
                    <span style={{ color: '#15803d', fontWeight: 600 }}>Assigned Department:</span>{' '}
                    <strong style={{ color: '#0f172a' }}>
                      {report.assigned_department || 'Pending SMS Review'}
                    </strong>
                  </div>
                  <div>
                    <span style={{ color: '#15803d', fontWeight: 600 }}>
                      Statutory ECCAIRS MOR (72h):
                    </span>{' '}
                    <strong
                      style={{
                        color: report.is_mandatory_occurrence ? '#dc2626' : '#475569',
                      }}
                    >
                      {report.is_mandatory_occurrence
                        ? 'YES (Statutory Filing)'
                        : 'NO (Internal / Voluntary)'}
                    </strong>
                  </div>
                  {report.eccairs_deadline && (
                    <div>
                      <span style={{ color: '#dc2626', fontWeight: 600 }}>ECCAIRS Deadline:</span>{' '}
                      <strong>{new Date(report.eccairs_deadline).toLocaleString()}</strong>
                    </div>
                  )}
                </div>
              </div>

              {/* Dynamic Questionnaire Responses */}
              {report.context_data &&
                Object.keys(report.context_data).filter(
                  (k) => !k.startsWith('reporter_selected_')
                ).length > 0 && (
                  <div
                    style={{
                      padding: '14px 16px',
                      background: '#ffffff',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <h4
                      style={{
                        margin: '0 0 10px 0',
                        fontSize: '12px',
                        color: '#475569',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                      }}
                    >
                      📋 Dynamic Questionnaire Responses
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {Object.entries(report.context_data)
                        .filter(([k]) => !k.startsWith('reporter_selected_'))
                        .map(([key, val]) => (
                          <div
                            key={key}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              padding: '6px 10px',
                              background: '#f8fafc',
                              borderRadius: '4px',
                              fontSize: '12px',
                            }}
                          >
                            <span style={{ color: '#64748b', fontWeight: 600 }}>
                              {key.replace(/_/g, ' ').toUpperCase()}:
                            </span>
                            <span style={{ color: '#0f172a', fontWeight: 500 }}>
                              {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

              {/* Reporter Identity Protection */}
              <div
                style={{
                  padding: '12px 16px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '12px',
                }}
              >
                <span style={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                  🛡️ Reporter Identity Protection:{' '}
                </span>
                {report.identity_mode === 'ANONYMOUS' ? (
                  <span style={{ color: '#059669', fontWeight: 600 }}>
                    Protected in Anonymous Vault. Identity is cryptographically isolated.
                  </span>
                ) : report.identity_mode === 'CONFIDENTIAL' ? (
                  <span style={{ color: '#2563eb', fontWeight: 600 }}>
                    Confidential report. Identity is quarantined and restricted to safety officers.
                  </span>
                ) : (
                  <span style={{ color: '#334155' }}>
                    Filed as Identified Report:{' '}
                    {report.reporter_identity?.full_name || report.created_by || 'Personnel'}
                    {report.reporter_identity?.email
                      ? ` (${report.reporter_identity.email})`
                      : ''}
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleOpenMailModal}
                  style={{
                    marginLeft: '10px',
                    padding: '2px 8px',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#2563eb',
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                  title="Send Direct Email to Reporter"
                >
                  <Mail size={12} />
                  <span>Mail to Reporter</span>
                </button>
              </div>

              {/* Status Progression Timeline */}
              <div
                style={{
                  padding: '14px 16px',
                  background: '#ffffff',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                }}
              >
                <h4
                  style={{
                    margin: '0 0 12px 0',
                    fontSize: '12px',
                    color: '#0f172a',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <Clock size={14} /> Lifecycle Progression & Timeline History
                </h4>
                {!report.status_history || report.status_history.length === 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '13px',
                      color: '#64748b',
                    }}
                  >
                    <CheckCircle2 size={16} color="#3b82f6" />
                    <span>
                      Current Status: <strong>{report.status}</strong> — Intake record validated.
                    </span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {report.status_history.map((hist, idx) => (
                      <div
                        key={hist.id || idx}
                        style={{
                          display: 'flex',
                          gap: '12px',
                          alignItems: 'flex-start',
                          fontSize: '12px',
                        }}
                      >
                        <div
                          style={{
                            minWidth: '130px',
                            color: '#94a3b8',
                            fontSize: '11px',
                            paddingTop: '2px',
                          }}
                        >
                          {hist.created_at ? new Date(hist.created_at).toLocaleString() : ''}
                        </div>
                        <div
                          style={{
                            flex: 1,
                            background: '#f8fafc',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid #e2e8f0',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginBottom: '2px',
                            }}
                          >
                            <strong style={{ color: '#1e293b' }}>
                              {hist.from_status === hist.to_status
                                ? `📝 Report Details Updated (${hist.to_status === 'SUBMITTED' ? 'Waiting Assessment' : hist.to_status})`
                                : `${hist.from_status !== 'NONE' ? `${hist.from_status} ➔ ` : ''}${hist.to_status === 'SUBMITTED' ? 'Waiting Assessment' : hist.to_status}`}
                            </strong>
                            <span style={{ color: '#64748b', fontSize: '11px' }}>
                              By: {hist.changed_by || 'System'}
                            </span>
                          </div>
                          {hist.reason && (
                            <div style={{ color: '#475569', fontSize: '12px' }}>{hist.reason}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: '14px 24px',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8fafc',
          }}
        >
          <div>
            {report && onNavigateToIngestion && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onNavigateToIngestion(report.id);
                }}
                className="btn-secondary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '7px 14px',
                  fontSize: '13px',
                  fontWeight: 600,
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  color: '#1e293b',
                  cursor: 'pointer',
                }}
              >
                <span>Open in Ingestion Module</span>
                <ExternalLink size={14} />
              </button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {report && (
              <button
                type="button"
                onClick={handleOpenMailModal}
                className="btn-secondary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '7px 14px',
                  fontSize: '13px',
                  fontWeight: 600,
                  backgroundColor: '#eff6ff',
                  border: '1px solid #93c5fd',
                  borderRadius: '6px',
                  color: '#1d4ed8',
                  cursor: 'pointer',
                }}
                title="Send official direct email feedback or inquiry to the reporter"
              >
                <Mail size={14} />
                <span>Mail to Reporter</span>
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="btn-primary"
              style={{
                padding: '7px 20px',
                fontSize: '13px',
                fontWeight: 600,
                borderRadius: '6px',
                cursor: 'pointer',
                backgroundColor: '#2563eb',
                color: '#ffffff',
                border: 'none',
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Mail to Reporter Modal Dialog */}
      {showMailModal && report && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(3px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            padding: '16px',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isSendingMail) {
              setShowMailModal(false);
            }
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              maxWidth: '680px',
              width: '100%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '90vh',
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '18px 24px',
                borderBottom: '1px solid #e2e8f0',
                background: '#f8fafc',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    background: '#dbeafe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#1d4ed8',
                  }}
                >
                  <Mail size={20} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                    Mail to Reporter
                  </h3>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                    Report #{report.report_number} • {report.title}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => !isSendingMail && setShowMailModal(false)}
                disabled={isSendingMail}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: isSendingMail ? 'not-allowed' : 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSendMailToReporter} style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
              <div
                style={{
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  marginBottom: '16px',
                  fontSize: '12px',
                  color: '#1e40af',
                  lineHeight: 1.5,
                }}
              >
                Dispatches an official email to the reporter via the SMS Notification Gateway. The message is permanently recorded in the report timeline, audit trail, and communication history.
              </div>

              {mailStatusMsg && (
                <div
                  style={{
                    padding: '10px 14px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    fontWeight: 600,
                    background: mailStatusMsg.type === 'success' ? '#f0fdf4' : '#fef2f2',
                    border: `1px solid ${mailStatusMsg.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
                    color: mailStatusMsg.type === 'success' ? '#166534' : '#991b1b',
                  }}
                >
                  {mailStatusMsg.text}
                </div>
              )}

              {/* Recipient Email Field */}
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                  Reporter Email Address *
                </label>
                <input
                  type="email"
                  value={mailRecipient}
                  onChange={(e) => setMailRecipient(e.target.value)}
                  placeholder="reporter@airline.com"
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '13px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontFamily: 'monospace',
                  }}
                />
              </div>

              {/* Subject Field */}
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                  Email Subject *
                </label>
                <input
                  type="text"
                  value={mailSubject}
                  onChange={(e) => setMailSubject(e.target.value)}
                  placeholder="Subject of the email..."
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '13px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                  }}
                />
              </div>

              {/* Quick Preset Templates */}
              <div style={{ marginBottom: '14px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                  Quick Response Templates:
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => handleApplyMailTemplate('DETAILS')}
                    className="btn-secondary"
                    style={{ fontSize: '11px', padding: '4px 10px', width: 'auto', background: '#f8fafc' }}
                  >
                    📝 Request Additional Details
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyMailTemplate('ASSESSMENT')}
                    className="btn-secondary"
                    style={{ fontSize: '11px', padding: '4px 10px', width: 'auto', background: '#f8fafc' }}
                  >
                    🔍 Under Active Assessment
                  </button>
                  <button
                    type="button"
                    onClick={() => handleApplyMailTemplate('ACTION')}
                    className="btn-secondary"
                    style={{ fontSize: '11px', padding: '4px 10px', width: 'auto', background: '#f8fafc' }}
                  >
                    ⚡ Occurrence & CAPA Dispatched
                  </button>
                </div>
              </div>

              {/* Message Body Field */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                  Feedback / Reply Message *
                </label>
                <textarea
                  value={mailMessage}
                  onChange={(e) => setMailMessage(e.target.value)}
                  placeholder="Type your official response or feedback message to the frontline reporter..."
                  required
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '13px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    lineHeight: 1.5,
                  }}
                />
              </div>

              {/* Modal Actions */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '10px',
                  borderTop: '1px solid #e2e8f0',
                  paddingTop: '16px',
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowMailModal(false)}
                  disabled={isSendingMail}
                  className="btn-secondary"
                  style={{ width: 'auto', padding: '8px 16px', fontSize: '13px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSendingMail}
                  className="btn-primary"
                  style={{
                    width: 'auto',
                    padding: '8px 20px',
                    fontSize: '13px',
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: isSendingMail ? 'not-allowed' : 'pointer',
                    background: '#2563eb',
                  }}
                >
                  <Send size={15} />
                  <span>{isSendingMail ? 'Dispatching Email...' : 'Send Mail to Reporter'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
